import { useState, useEffect, useRef } from "react";
import {
  fetchAttendanceList, 
  createAttendance, updateAttendance, deleteAttendance,
  fetchApplyList, scanAttend, deleteApply,
  fetchUnfinishedMembers, fetchEducationNoShow
} from "../api/attendanceApi";
import Toast from "../components/Toast";
import { getCodeList } from "../api/codeApi";

const Badge = ({ children, color }) => {
  const colors = {
    blue  : { bg: "#e8f4fd", text: "#1677ff", border: "#bae0ff" },
    green : { bg: "#f6ffed", text: "#52c41a", border: "#b7eb8f" },
    red   : { bg: "#fff2f0", text: "#ff4d4f", border: "#ffccc7" },
    gray  : { bg: "#f5f5f5", text: "#888",    border: "#d9d9d9" },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
      {children}
    </span>
  );
};

// 출석구분 색상 매핑 (교육/커피차/이벤트/총회/기타)
const ATT_TYPE_COLOR_MAP = {
  '교육'  : { bg: "#e6f4ff", text: "#1677ff", border: "#91caff" },
  '커피차': { bg: "#fff7e6", text: "#fa8c16", border: "#ffd591" },
  '이벤트': { bg: "#fff0f6", text: "#eb2f96", border: "#ffadd2" },
  '총회'  : { bg: "#f9f0ff", text: "#722ed1", border: "#d3adf7" },
  '기타'  : { bg: "#f5f5f5", text: "#888",    border: "#d9d9d9" },
};

const AttTypeBadge = ({ type }) => {
  const c = ATT_TYPE_COLOR_MAP[type] || ATT_TYPE_COLOR_MAP['기타'];
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
      {type}
    </span>
  );
};

// 교육 구분 색상 매핑 (F:집체, O:온라인, B:집체+온라인)
const EDU_TYPE_MAP = {
  F: { label: "집체",       bg: "#f6ffed", text: "#52c41a", border: "#b7eb8f" },
  O: { label: "온라인",     bg: "#e6f4ff", text: "#1677ff", border: "#91caff" },
  B: { label: "집체+온라인", bg: "#fff7e6", text: "#fa8c16", border: "#ffd591" },
};

const EduTypeBadge = ({ type }) => {
  const c = EDU_TYPE_MAP[type] || EDU_TYPE_MAP.F;
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
      {c.label}
    </span>
  );
};

// ===================== 출석 스캔 페이지 =====================
const ScanPage = ({ item, onBack, isAdmin }) => {
  const [applyList,  setApplyList]  = useState([]);
  const [empNo,      setEmpNo]      = useState('');
  const [attRound,   setAttRound]   = useState(1);
  const [sortOrder,  setSortOrder]  = useState('desc');   // ← 추가 (기본 내림차순)
  const [toast,      setToast]      = useState('');
  const [lastScan,   setLastScan]   = useState(null);
  const [showVerify,      setShowVerify]      = useState(false);
  const [verifyList,      setVerifyList]      = useState([]);
  const [verifyLoading,   setVerifyLoading]   = useState(false);
  const [showNoShow,    setShowNoShow]    = useState(false);
  const [noShowList,    setNoShowList]    = useState([]);
  const [noShowLoading, setNoShowLoading] = useState(false);
  const inputRef = useRef(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadApplyList = async () => {
    const res = await fetchApplyList(item.att_year, item.seq_no);
    if (res.success) setApplyList(res.data);
  };

  useEffect(() => {
    loadApplyList();
    // 스캔 입력창 자동 포커스 유지
    const focusInterval = setInterval(() => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    }, 500);
    return () => clearInterval(focusInterval);
  }, []);

  const handleScan = async (e) => {
    if (e.key !== 'Enter') return;
    const scannedEmpNo = empNo.trim();
    if (!scannedEmpNo) return;

    const res = await scanAttend(item.att_year, item.seq_no, scannedEmpNo, attRound);
    if (res.success) {
      setLastScan({ name: res.data.name, emp_no: res.data.emp_no, att_round: attRound, success: true });
      //showToast(`✅ ${res.data.name} (${attRound}회차) 출석 완료!`);
      loadApplyList();
    } else {
      setLastScan({ name: scannedEmpNo, success: false, message: res.message });
      //showToast(`❌ ${res.message}`);
    }
    setEmpNo('');
  };

  const handleDeleteApply = async (emp_no, att_round) => {
    const res = await deleteApply(item.att_year, item.seq_no, emp_no, att_round);
    if (res.success) { showToast('✅ 출석이 취소되었습니다.'); loadApplyList(); }
    else showToast(`❌ ${res.message}`);
  };

  // 회차별 출석자 엑셀 다운로드
  const handleExcelDownload = () => {
    const currentRoundList = roundGroups.find(g => g.round === attRound)?.list || [];
    if (currentRoundList.length === 0) {
      showToast('❌ 다운로드할 출석자가 없습니다.');
      return;
    }
    import("xlsx").then((XLSX) => {
      const headers = ["No", "소속", "부서", "직종", "사번", "성명", "출석시간"];
      const rows = currentRoundList.map((a, i) => [
        i + 1,
        a.division_sub_name || '-',
        a.department        || '-',
        a.job_name          || '-',
        a.emp_no,
        a.name              || '-',
        a.attend_time
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = [{ wch: 6 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 22 }]
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${attRound}회차 출석`);
      XLSX.writeFile(wb, `출석_${item.att_title}_${attRound}회차.xlsx`);
    });
  };

  // 출석검증 실시
  const handleVerify = async () => {
    console.log('attRound:', attRound, typeof attRound);
    setShowVerify(true);
    setVerifyLoading(true);
    try {
      const res = await fetchUnfinishedMembers(item.att_year, item.seq_no, attRound);
      if (res.success) setVerifyList(res.data);
      else showToast(`❌ ${res.message}`);
    } catch (err) {
      showToast('❌ 검증 조회 실패');
    } finally {
      setVerifyLoading(false);
    }
  };

  // 검증결과 다운로드
  const handleVerifyExcelDownload = () => {
    if (verifyList.length === 0) { showToast('❌ 다운로드할 데이터가 없습니다.'); return; }
    import("xlsx").then((XLSX) => {
      const headers = ["No", "소속", "부서", "직종", "사번", "이름", "연락처", "출석횟수"];
      const rows = verifyList.map((m, i) => [
        i + 1,
        m.division_sub_name || '-',
        m.department        || '-',
        m.job_name          || '-',
        m.emp_no,
        m.name,
        m.phone             || '-',
        `${m.attended_cnt}/${item.att_cnt}`
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = [{ wch: 5 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 10 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '출석미완료');
      XLSX.writeFile(wb, `출석검증_${item.att_title}.xlsx`);
    });
  };

  // 미출석자 검증
  const handleNoShow = async () => {
    setShowNoShow(true);
    setNoShowLoading(true);
    try {
      const res = await fetchEducationNoShow(item.att_year, item.seq_no);
      if (res.success) setNoShowList(res.data);
      else showToast(`❌ ${res.message}`);
    } catch (err) {
      showToast('❌ 조회 실패');
    } finally {
      setNoShowLoading(false);
    }
  };

  // 미출석자 다운로드
  const handleNoShowExcelDownload = () => {
    if (noShowList.length === 0) { showToast('❌ 다운로드할 데이터가 없습니다.'); return; }
    import("xlsx").then((XLSX) => {
      const headers = ["No", "소속", "부서", "직종", "사번", "이름", "연락처"];
      const rows = noShowList.map((m, i) => [
        i + 1,
        m.division_sub_name || '-',
        m.department        || '-',
        m.job_name          || '-',
        m.emp_no,
        m.name,
        m.phone             || '-'
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = [{ wch: 5 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 16 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '교육노쇼');
      XLSX.writeFile(wb, `교육노쇼_${item.att_title}.xlsx`);
    });
  };

  
  // 회차별 출석자 그룹핑 (출석시간 기준 정렬 적용)
  const roundGroups = Array.from({ length: Number(item.att_cnt) }, (_, i) => {
    const round = i + 1;
    const roundList = applyList.filter(a => Number(a.att_round) === round);
    const sortedList = [...roundList].sort((a, b) => {
      const cmp = (a.attend_time || '').localeCompare(b.attend_time || '');
      return sortOrder === 'desc' ? -cmp : cmp;
    });
    return { round, list: sortedList };
  });

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />
      <button style={styles.backBtn} onClick={onBack}>← 목록으로</button>

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>📋 {item.att_title}</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge color="blue">{item.att_dt}</Badge>
          <AttTypeBadge type={item.att_type} />
        </div>
      </div>

      {/* 스캔 영역 */}
      <div style={{ ...styles.card, padding: 24, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1a1a2e", margin: "0 0 16px" }}>🔍 바코드 스캔</h3>

        {/* 회차 선택 라디오 */}
        <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          {Array.from({ length: item.att_cnt }, (_, i) => i + 1).map(round => (
            <label key={round} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 14, fontWeight: attRound === round ? 700 : 400, color: attRound === round ? "#1677ff" : "#666" }}>
              <input
                type="radio"
                name="attRound"
                value={round}
                checked={attRound === round}
                onChange={() => setAttRound(round)}
                style={{ cursor: "pointer" }}
              />
              {round}회차
              <Badge color={attRound === round ? "blue" : "gray"}>
                {roundGroups[round-1]?.list.length || 0}명
              </Badge>
            </label>
          ))}
        </div>

        {/* 스캔 입력 */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            ref={inputRef}
            style={{ flex: 1, border: "2px solid #1677ff", borderRadius: 10, padding: "14px 16px", fontSize: 16, outline: "none", background: "#f0f7ff" }}
            placeholder={`사번을 스캔하세요 (${attRound}회차)`}
            value={empNo}
            onChange={e => setEmpNo(e.target.value)}
            onKeyDown={handleScan}
            autoFocus
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            {/* 교육 노쇼 버튼 */}
            <button
              style={{
                background: "#fff",
                border: `1.5px solid ${item.edu_year ? "#1677ff" : "#d9d9d9"}`,
                color: item.edu_year ? "#1677ff" : "#bbb",
                borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 600,
                cursor: item.edu_year ? "pointer" : "not-allowed"
              }}
              onClick={handleNoShow}
              disabled={!item.edu_year}
              title={!item.edu_year ? "교육 연동 시에만 사용 가능합니다." : ""}
            >📋 교육 노쇼</button>

            {/* 출석검증 버튼 */}
            <button
              style={{
                background: "#fff",
                border: `1.5px solid ${attRound < 2 ? "#d9d9d9" : "#fa8c16"}`,
                color: attRound < 2 ? "#bbb" : "#fa8c16",
                borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 600,
                cursor: attRound < 2 ? "not-allowed" : "pointer"
              }}
              onClick={handleVerify}
              disabled={attRound < 2}
              title={attRound < 2 ? "2회차 이상부터 검증 가능합니다." : ""}
            >🔎 출석 검증</button>

            {/* 엑셀 다운로드 버튼 */}
            <button
              style={{
                background: "#fff",
                border: "1.5px solid #52c41a", color: "#52c41a",
                borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 600,
                cursor: "pointer"
              }}
              onClick={handleExcelDownload}
            >📊 {attRound}회차 엑셀</button>
          </div>
        </div>

        {/* 최근 스캔 결과 */}
        {lastScan && (
          <div style={{
            marginTop: 12, padding: "12px 16px", borderRadius: 8,
            background: lastScan.success ? "#f6ffed" : "#fff2f0",
            border: `1px solid ${lastScan.success ? "#b7eb8f" : "#ffccc7"}`,
            fontSize: 14, fontWeight: 600,
            color: lastScan.success ? "#52c41a" : "#ff4d4f"
          }}>
            {lastScan.success
              ? `✅ ${lastScan.name} (${lastScan.emp_no}) - ${lastScan.att_round}회차 출석 완료`
              : `❌ ${lastScan.message}`
            }
          </div>
        )}
      </div>

      {/* 회차별 출석자 리스트 */}
      {roundGroups
        .filter(({ round }) => round === attRound)  // ← 선택된 회차만
        .map(({ round, list }) => (
        <div key={round} style={{ ...styles.card, marginBottom: 12, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #f0f0f0" }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>
              {round}회차 출석자
            </h4>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ display: "flex", gap: 10 }}>
                {[{ value: 'desc', label: '내림차순' }, { value: 'asc', label: '오름차순' }].map(opt => (
                  <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 12, fontWeight: sortOrder === opt.value ? 700 : 400, color: sortOrder === opt.value ? "#1677ff" : "#888" }}>
                    <input
                      type="radio"
                      name={`sortOrder-${round}`}
                      value={opt.value}
                      checked={sortOrder === opt.value}
                      onChange={() => setSortOrder(opt.value)}
                      style={{ cursor: "pointer" }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              <Badge color={list.length > 0 ? "green" : "gray"}>{list.length}명</Badge>
            </div>
          </div>
          {list.length === 0 ? (
            <div style={styles.empty}>출석자가 없습니다.</div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1.5fr 1.5fr 1fr 1.5fr 2fr 0.8fr", padding: "10px 20px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" }}>
                {["No", "소속", "부서", "직종", "사번", "성명", "출석시간", isAdmin ? "취소" : ""].map((h, i) => (
                  <div key={i} style={{ fontSize: 12, fontWeight: 700, color: "#888" }}>{h}</div>
                ))}
              </div>
              {list.map((a, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1.5fr 1.5fr 1fr 1.5fr 2fr 0.8fr", padding: "12px 20px", borderBottom: "1px solid #f5f5f5", alignItems: "center" }}>
                  <div style={{ fontSize: 14, color: "#888" }}>{i + 1}</div>
                  <div style={{ fontSize: 13, color: "#1a1a2e" }}>{a.division_sub_name || '-'}</div>
                  <div style={{ fontSize: 13, color: "#1a1a2e" }}>{a.department || '-'}</div>
                  <div style={{ fontSize: 13, color: "#1a1a2e" }}>{a.job_name   || '-'}</div>
                  <div style={{ fontSize: 13, color: "#1a1a2e" }}>{a.emp_no}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{a.name || '-'}</div>
                  <div style={{ fontSize: 13, color: "#888" }}>{a.attend_time}</div>
                  {isAdmin && (
                    <div>
                      <button
                        style={{ background: "#fff2f0", border: "1px solid #ffccc7", color: "#ff4d4f", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
                        onClick={() => handleDeleteApply(a.emp_no, round)}
                      >취소</button>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      ))}

      {/* 출석 검증 모달 */}
      {showVerify && (
        <div style={ms.overlay}>
          <div style={{ ...ms.modal, maxWidth: 780, padding: 0 }}>
            {/* 헤더 */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: "0 0 4px" }}>
                  🔎 출석 검증 - {item.att_title}
                </h3>
                <p style={{ fontSize: 12, color: "#aaa", margin: 0 }}>
                  {attRound - 1}회차 출석 완료 후 {attRound}회차 미출석 조합원
                </p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ background: "#fff", border: "1.5px solid #52c41a", color: "#52c41a", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  onClick={handleVerifyExcelDownload}>
                  📊 엑셀 다운로드
                </button>
                <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
                  onClick={() => setShowVerify(false)}>✕</button>
              </div>
            </div>

            {/* 목록 */}
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {verifyLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb" }}>⏳ 불러오는 중...</div>
              ) : verifyList.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#52c41a", fontSize: 15, fontWeight: 600 }}>
                  ✅ 모든 조합원이 출석을 완료했습니다!
                </div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1.5fr 1.2fr 1fr 1.2fr 1.5fr 0.8fr", padding: "10px 24px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" }}>
                    {["No", "소속", "부서", "직종", "사번", "이름", "연락처", "출석"].map(h => (
                      <div key={h} style={{ fontSize: 12, fontWeight: 700, color: "#888" }}>{h}</div>
                    ))}
                  </div>
                  {verifyList.map((m, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1.5fr 1.2fr 1fr 1.2fr 1.5fr 0.8fr", padding: "12px 24px", borderBottom: "1px solid #f5f5f5", alignItems: "center" }}>
                      <div style={{ fontSize: 13, color: "#888" }}>{i + 1}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.division_sub_name || '-'}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.department || '-'}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.job_name   || '-'}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.emp_no}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{m.name}</div>
                      <div style={{ fontSize: 13, color: "#888" }}>{m.phone || '-'}</div>
                      <div>
                        <span style={{
                          fontSize: 12, fontWeight: 600,
                          color: m.attended_cnt === 0 ? "#ff4d4f" : "#fa8c16"
                        }}>
                          {m.attended_cnt}/{item.att_cnt}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#ff4d4f", fontWeight: 600 }}>
                미완료 {verifyList.length}명
              </span>
              <button style={ms.cancelBtn} onClick={() => setShowVerify(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 교육 노쇼 모달 */}
      {showNoShow && (
        <div style={ms.overlay}>
          <div style={{ ...ms.modal, maxWidth: 780, padding: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: "0 0 4px" }}>
                  📋 교육 노쇼 - {item.att_title}
                </h3>
                <p style={{ fontSize: 12, color: "#aaa", margin: 0 }}>
                  교육 신청 후 1회차 미출석 조합원
                </p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{ background: "#fff", border: "1.5px solid #52c41a", color: "#52c41a", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  onClick={handleNoShowExcelDownload}>
                  📊 엑셀 다운로드
                </button>
                <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
                  onClick={() => setShowNoShow(false)}>✕</button>
              </div>
            </div>

            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {noShowLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb" }}>⏳ 불러오는 중...</div>
              ) : noShowList.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#52c41a", fontSize: 15, fontWeight: 600 }}>
                  ✅ 모든 신청자가 출석을 완료했습니다!
                </div>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1.5fr 1.2fr 1fr 1.2fr 1.5fr", padding: "10px 24px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" }}>
                    {["No", "소속", "부서", "직종", "사번", "이름", "연락처"].map(h => (
                      <div key={h} style={{ fontSize: 12, fontWeight: 700, color: "#888" }}>{h}</div>
                    ))}
                  </div>
                  {noShowList.map((m, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1.5fr 1.2fr 1fr 1.2fr 1.5fr", padding: "12px 24px", borderBottom: "1px solid #f5f5f5", alignItems: "center" }}>
                      <div style={{ fontSize: 13, color: "#888" }}>{i + 1}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.division_sub_name || '-'}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.department || '-'}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.job_name   || '-'}</div>
                      <div style={{ fontSize: 13, color: "#1a1a2e" }}>{m.emp_no}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{m.name}</div>
                      <div style={{ fontSize: 13, color: "#888" }}>{m.phone || '-'}</div>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#1677ff", fontWeight: 600 }}>
                노쇼 {noShowList.length}명
              </span>
              <button style={ms.cancelBtn} onClick={() => setShowNoShow(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== 등록/수정 모달 =====================
const FormModal = ({ data, onClose, onSave }) => {
  const attTypeList = getCodeList('ATT_TYPE'); // ← 추가
  const isNew = !data;
  const [form, setForm] = useState(data || {
    att_type: attTypeList[0]?.code || '교육', // ← 첫 번째 코드로 초기값
    att_title: '', att_dt: '', att_cnt: 1, edu_year: '', edu_seq_no: ''
  });
  const [showEduSearch, setShowEduSearch] = useState(false);
  const [eduList,       setEduList]       = useState([]);
  const [eduYear,       setEduYear]       = useState(new Date().getFullYear().toString());
  const [eduLoading,    setEduLoading]    = useState(false);
  const [eduStep,       setEduStep]       = useState(1);    // 1: 교육선택, 2: 세부일정선택
  const [selectedEdu,   setSelectedEdu]   = useState(null); // 선택된 교육 메인
  const [eduDetailList, setEduDetailList] = useState([]);   // 세부일정 목록
  const [detailLoading, setDetailLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleEduSearch = async (year) => {
    setEduLoading(true);
    try {
      const BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/education?edu_year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setEduList(data.data);
    } catch (err) {}
    finally { setEduLoading(false); }
  };

  // 1단계: 교육 메인 선택 → 2단계로 이동
  const handleEduSelect = async (edu) => {
    setSelectedEdu(edu);
    setEduStep(2);
    setDetailLoading(true);
    try {
      const BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/education/${edu.edu_year}/${edu.seq_no}/detail`, {  // ← details → detail
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setEduDetailList(data.data);
    } catch (err) {}
    finally { setDetailLoading(false); }
  };

  // 2단계: 세부일정 선택 → 연동 완료
  const handleEduDetailSelect = (detail) => {
    set('edu_year',   selectedEdu.edu_year);
    set('edu_seq_no', String(selectedEdu.seq_no));
    set('att_dt',     detail.edu_dt);  // ← 출석일자 자동 세팅
    if (!form.att_title) set('att_title', selectedEdu.edu_name);
    setShowEduSearch(false);
    setEduStep(1);
    setSelectedEdu(null);
    setEduDetailList([]);
  };

  return (
    <div style={ms.overlay}>
      <div style={ms.modal}>
        <div style={ms.header}>
          <span style={ms.title}>{isNew ? '📋 출석 등록' : '✏️ 출석 수정'}</span>
          <button style={ms.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={ms.body}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...ms.field, flex: 1 }}>
              <label style={ms.label}>출석구분 <span style={{ color: "#ff4d4f" }}>*</span></label>
              <select style={ms.input} value={form.att_type} onChange={e => set('att_type', e.target.value)}>
                {attTypeList.map(c => (
                  <option key={c.code} value={c.code}>{c.code_nm}</option>
                ))}
              </select>
            </div>
            <div style={{ ...ms.field, width: 80 }}>
              <label style={ms.label}>출석횟수 <span style={{ color: "#ff4d4f" }}>*</span></label>
              <input style={ms.input} type="number" min={1} max={9} value={form.att_cnt}
                onChange={e => set('att_cnt', Number(e.target.value))} />
            </div>
          </div>

          <div style={ms.field}>
            <label style={ms.label}>출석제목 <span style={{ color: "#ff4d4f" }}>*</span></label>
            <input style={ms.input} placeholder="출석 제목을 입력하세요" value={form.att_title}
              onChange={e => set('att_title', e.target.value)} />
          </div>

          <div style={ms.field}>
            <label style={ms.label}>출석일자 <span style={{ color: "#ff4d4f" }}>*</span></label>
            <input style={ms.input} type="date" value={form.att_dt}
              onChange={e => set('att_dt', e.target.value)} />
          </div>

          {/* 교육 연동 - 출석구분이 교육일 때만 */}
          <div style={ms.field}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <label style={{ ...ms.label, margin: 0 }}>교육 연동</label>
              <button
                style={{
                  background: "none", border: "none", padding: "0 2px",
                  fontSize: 16, cursor: form.att_type === '교육' ? "pointer" : "not-allowed",
                  color: form.att_type === '교육' ? "#1677ff" : "#ccc"
                }}
                onClick={() => {
                  if (form.att_type !== '교육') return;
                  setEduStep(1); setShowEduSearch(true); handleEduSearch(eduYear);
                }}
                title={form.att_type !== '교육' ? "출석구분이 '교육'일 때만 사용 가능합니다." : "교육 검색"}
              >🔍</button>
              {form.edu_year && (
                <button
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#ff4d4f", padding: "0 2px" }}
                  onClick={() => { set('edu_year', ''); set('edu_seq_no', ''); }}
                  title="연동 해제"
                >✕</button>
              )}
            </div>
            <input
              style={{ ...ms.input, background: "#f5f5f5", color: form.edu_year ? "#1a1a2e" : "#aaa" }}
              value={form.edu_year ? `${form.edu_year}년 ${form.edu_seq_no}차` : '교육을 선택하세요 (선택사항)'}
              disabled
            />
          </div>
        </div>
        <div style={ms.footer}>
          <button style={ms.cancelBtn} onClick={onClose}>취소</button>
          <button style={ms.saveBtn} onClick={() => onSave(form)}>
            {isNew ? '등록' : '저장'}
          </button>
        </div>
      </div>

      {/* 교육 검색 모달 */}
      {showEduSearch && (
        <div style={ms.overlay}>
          <div style={{ ...ms.modal, maxWidth: 500, padding: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: "0 0 2px" }}>
                  📚 교육 연동 {eduStep === 1 ? '- 교육 선택' : '- 세부일정 선택'}
                </h3>
                {eduStep === 2 && selectedEdu && (
                  <p style={{ fontSize: 12, color: "#1677ff", margin: 0 }}>
                    {selectedEdu.edu_year}년 {selectedEdu.seq_no}차 {selectedEdu.edu_name}
                  </p>
                )}
              </div>
              <button style={{ background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" }}
                onClick={() => { setShowEduSearch(false); setEduStep(1); setSelectedEdu(null); }}>✕</button>
            </div>

            {/* 1단계 - 교육 메인 선택 */}
            {eduStep === 1 && (
              <>
                <div style={{ padding: "12px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    style={{ border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 14, outline: "none", width: 100 }}
                    value={eduYear}
                    onChange={e => setEduYear(e.target.value)}
                    placeholder="연도"
                    maxLength={4}
                  />
                  <button
                    style={{ background: "#1677ff", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                    onClick={() => handleEduSearch(eduYear)}
                  >{eduLoading ? '조회 중...' : '조회'}</button>
                </div>
                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                  {eduList.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontSize: 14 }}>
                      {eduLoading ? '⏳ 불러오는 중...' : '교육 목록이 없습니다.'}
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 3fr 1.5fr", padding: "10px 24px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" }}>
                        {["연도/연번", "교육명", "기간"].map(h => (
                          <div key={h} style={{ fontSize: 12, fontWeight: 700, color: "#888" }}>{h}</div>
                        ))}
                      </div>
                      {eduList.map((edu, i) => (
                        <div key={i}
                          style={{ display: "grid", gridTemplateColumns: "1fr 3fr 1.5fr", padding: "14px 24px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", transition: "background 0.15s" }}
                          onClick={() => handleEduSelect(edu)}
                          onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                          onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1677ff" }}>{edu.edu_year}-{edu.seq_no}</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{edu.edu_name}</div>
                          <div style={{ fontSize: 12, color: "#888" }}>{edu.start_dt}~{edu.end_dt}</div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}

            {/* 2단계 - 세부일정 선택 */}
            {eduStep === 2 && (
              <>
                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                  {detailLoading ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontSize: 14 }}>⏳ 불러오는 중...</div>
                  ) : eduDetailList.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#bbb", fontSize: 14 }}>세부일정이 없습니다.</div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "10px 24px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" }}>
                        {["교육일자", "신청인원"].map(h => (
                          <div key={h} style={{ fontSize: 12, fontWeight: 700, color: "#888" }}>{h}</div>
                        ))}
                      </div>
                      {eduDetailList.map((detail, i) => (
                        <div key={i}
                          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "14px 24px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", transition: "background 0.15s" }}
                          onClick={() => handleEduDetailSelect(detail)}
                          onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                          onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{detail.edu_dt}</div>
                          <div style={{ fontSize: 13, color: "#888" }}>{detail.apply_cnt}명</div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", textAlign: "left" }}>
                  <button
                    style={{ background: "none", border: "none", color: "#1677ff", fontSize: 13, cursor: "pointer", fontWeight: 600 }}
                    onClick={() => { setEduStep(1); setSelectedEdu(null); setEduDetailList([]); }}>
                    ← 교육 다시 선택
                  </button>
                </div>
              </>
            )}

            <div style={{ padding: "14px 24px", borderTop: "1px solid #f0f0f0", textAlign: "right" }}>
              <button style={ms.cancelBtn} onClick={() => { setShowEduSearch(false); setEduStep(1); setSelectedEdu(null); }}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== 목록 페이지 =====================
export default function AttendancePage({ onLogout, userLevel }) {
  const isAdmin = ['0', '1'].includes(userLevel);
  const [page,       setPage]       = useState('list');
  const [list,       setList]       = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [typeFilter, setTypeFilter] = useState('');
  const [titleSearch, setTitleSearch] = useState('');
  const [dateSearch, setDateSearch] = useState('');
  const [listSortOrder, setListSortOrder] = useState('desc');
  const [showForm,   setShowForm]   = useState(false);
  const [editData,   setEditData]   = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [yearList, setYearList] = useState([]);
  const [attTypeFilterList, setAttTypeFilterList] = useState([]);
  const PAGE_SIZE = 10;

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const loadList = async (year) => {
    setLoading(true);
    try {
      const res = await fetchAttendanceList(year);
      if (res.success) setList(res.data);
      else if (res.message === '토큰이 만료되었습니다. 다시 로그인해주세요.') onLogout();
    } catch (err) { showToast('❌ 목록 조회 실패'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadList(yearFilter); }, [yearFilter]);
  useEffect(() => {
    setYearList(getCodeList('YEAR_LIST'));
    setAttTypeFilterList(getCodeList('ATT_TYPE'));
  }, []);

  const years = [...new Set(list.map(r => r.att_year))].sort((a, b) => b - a);
  if (years.length === 0) years.push(yearFilter);

  const filtered = list
  .filter(r =>
    (!typeFilter   || r.att_type === typeFilter) &&
    (!titleSearch  || r.att_title.includes(titleSearch)) &&
    (!dateSearch   || r.att_dt === dateSearch)
  )
  .sort((a, b) => {
    // 출석일자 우선, 같으면 연번(seq_no)으로 2차 정렬
    const cmp = (a.att_dt || '').localeCompare(b.att_dt || '') || (Number(a.seq_no) - Number(b.seq_no));
    return listSortOrder === 'desc' ? -cmp : cmp;
  });

  const paginated  = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const handleResetFilter = () => {
    setTypeFilter(''); setTitleSearch(''); setDateSearch(''); setCurrentPage(1);
  };

  const handleSave = async (form) => {
    try {
      const res = editData
        ? await updateAttendance(editData.att_year, editData.seq_no, form)
        : await createAttendance(form);
      if (res.success) {
        setShowForm(false); setEditData(null);
        loadList(yearFilter);
        showToast(editData ? '✅ 수정되었습니다.' : '✅ 등록되었습니다.');
      } else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 저장 실패'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.att_title}" 을 삭제하시겠습니까?`)) return;
    try {
      const res = await deleteAttendance(item.att_year, item.seq_no);
      if (res.success) { loadList(yearFilter); showToast('✅ 삭제되었습니다.'); }
      else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 삭제 실패'); }
  };

  // 통합 출석부 다운로드 (회차별 1회 이상 출석한 조합원 전체, 회차별 출석시간 피벗)
  const handleDownloadCombined = async (item) => {
    const res = await fetchApplyList(item.att_year, item.seq_no);
    if (!res.success) { showToast(`❌ ${res.message}`); return; }
    if (res.data.length === 0) { showToast('❌ 출석 데이터가 없습니다.'); return; }

    // emp_no 기준으로 회차별 출석시간 피벗
    const memberMap = new Map();
    res.data.forEach(r => {
      if (!memberMap.has(r.emp_no)) {
        memberMap.set(r.emp_no, {
          emp_no: r.emp_no,
          name: r.name,
          division_sub_name: r.division_sub_name,
          department: r.department,
          job_name: r.job_name,
          rounds: {}
        });
      }
      memberMap.get(r.emp_no).rounds[r.att_round] = r.attend_time;
    });

    const members = Array.from(memberMap.values())
      .sort((a, b) =>
        (a.division_sub_name || '').localeCompare(b.division_sub_name || '') ||
        (a.name || '').localeCompare(b.name || '')
      );

    const maxRound = Math.min(Number(item.att_cnt) || 1, 9); // 최대 9회차 제한

    import("xlsx").then((XLSX) => {
      const headers = ["No", "소속", "부서", "직종", "사번", "성명"];
      for (let r = 1; r <= maxRound; r++) headers.push(`${r}회차 출석시간`);

      const rows = members.map((m, i) => {
        const row = [
          i + 1,
          m.division_sub_name || '-',
          m.department || '-',
          m.job_name || '-',
          m.emp_no,
          m.name || '-',
        ];
        for (let r = 1; r <= maxRound; r++) row.push(m.rounds[r] || '-');
        return row;
      });

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const colWidths = [{ wch: 5 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
      for (let r = 0; r < maxRound; r++) colWidths.push({ wch: 20 });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '통합출석부');
      XLSX.writeFile(wb, `통합출석부_${item.att_title}.xlsx`);
    });
  };

  if (page === 'scan' && selected) {
    return <ScanPage item={selected} onBack={() => setPage('list')} isAdmin={isAdmin} />;
  }

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>📋 출석 관리</h1>
        <span style={styles.statBadge}>총 {filtered.length}건</span>
      </div>

      {/* 검색 필터 */}
      <div style={styles.filterCard}>
        <select style={styles.select} value={yearFilter}
          onChange={e => { setYearFilter(e.target.value); setCurrentPage(1); }}>
          <option value="">전체 연도</option>
          {yearList.map(y => (
            <option key={y.code} value={y.code}>{y.code_nm}</option>
          ))}
        </select>
        <select style={styles.select} value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
          <option value="">전체 구분</option>
          {attTypeFilterList.map(t => (
            <option key={t.code} value={t.code}>{t.code_nm}</option>
          ))}
        </select>
        <input style={{ ...styles.select, flex: 1, cursor: "text" }} placeholder="🔍 제목 검색" value={titleSearch}
          onChange={e => { setTitleSearch(e.target.value); setCurrentPage(1); }} />
        <input type="date" style={styles.select} value={dateSearch}
          onChange={e => { setDateSearch(e.target.value); setCurrentPage(1); }} />
        {(typeFilter || titleSearch || dateSearch) && (
          <button
            style={{ background: "#f5f5f5", border: "1px solid #d9d9d9", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#888", cursor: "pointer" }}
            onClick={handleResetFilter}>
            초기화
          </button>
        )}
      </div>

      {/* 정렬 옵션 */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, marginBottom: 10, padding: "0 4px" }}>
        {[{ value: 'desc', label: '최근순' }, { value: 'asc', label: '오래된순' }].map(opt => (
          <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 13, fontWeight: listSortOrder === opt.value ? 700 : 400, color: listSortOrder === opt.value ? "#1677ff" : "#888" }}>
            <input
              type="radio"
              name="listSortOrder"
              value={opt.value}
              checked={listSortOrder === opt.value}
              onChange={() => { setListSortOrder(opt.value); setCurrentPage(1); }}
              style={{ cursor: "pointer" }}
            />
            {opt.label}
          </label>
        ))}
      </div>

      {/* 목록 */}
      <div style={styles.card}>
        <div style={styles.tableHeader}>
          {["연번", "구분", "제목", "출석일자", "횟수", "교육연동", ""].map(h => (
            <div key={h} style={styles.th}>{h}</div>
          ))}
        </div>
        {loading ? (
          <div style={styles.empty}>⏳ 불러오는 중...</div>
        ) : paginated.length === 0 ? (
          <div style={styles.empty}>등록된 출석이 없습니다.</div>
        ) : paginated.map((item, i) => (
          <div key={i} style={styles.tableRow}>
            <div style={styles.td}>{item.seq_no}</div>
            <div style={styles.td}><AttTypeBadge type={item.att_type} /></div>
            <div style={{ ...styles.td, fontWeight: 600, cursor: "pointer", color: "#1677ff" }}
              onClick={() => { setSelected(item); setPage('scan'); }}>
              {item.att_title}
            </div>
            <div style={styles.td}>{item.att_dt}</div>
            <div style={styles.td}>{item.att_cnt}회</div>
            <div style={styles.td}>
              {item.edu_year
                ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Badge color="green">{item.edu_year}-{item.edu_seq_no}</Badge>
                    <EduTypeBadge type={item.edu_type} />
                  </div>
                )
                : '-'}
            </div>
            {(isAdmin) && (
              <div style={{ ...styles.td, display: "flex", gap: 6 }}>
                <button style={styles.smAttBtn} onClick={() => handleDownloadCombined(item)}>출석부</button>
                {isAdmin && (
                  <>
                    <button style={styles.smEditBtn} onClick={() => { setEditData(item); setShowForm(true); }}>수정</button>
                    <button style={styles.smDelBtn}  onClick={() => handleDelete(item)}>삭제</button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p}
                style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}
                onClick={() => setCurrentPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>

      {isAdmin && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button style={styles.registerBtn} onClick={() => { setEditData(null); setShowForm(true); }}>
            ＋ 출석 등록
          </button>
        </div>
      )}

      {showForm && (
        <FormModal
          data={editData}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

const styles = {
  wrap        : { background: "#f5f7fa", padding: 24, fontFamily: "'Noto Sans KR', sans-serif" },
  pageHeader  : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  pageTitle   : { fontSize: 22, fontWeight: 800, color: "#1a1a2e", margin: 0 },
  statBadge   : { background: "#e8f4fd", color: "#1677ff", border: "1px solid #bae0ff", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600 },
  filterCard  : { background: "#fff", borderRadius: 12, padding: "14px 16px", marginBottom: 12, display: "flex", gap: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  select      : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 14px", fontSize: 14, outline: "none", background: "#fff", cursor: "pointer" },
  card        : { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  tableHeader : { display: "grid", gridTemplateColumns: "0.5fr 1fr 2.2fr 1.2fr 0.7fr 1.2fr 1.5fr", padding: "12px 20px", background: "#f8faff", borderBottom: "1px solid #e8f0fe" },
  th          : { fontSize: 12, fontWeight: 700, color: "#888" },
  tableRow    : { display: "grid", gridTemplateColumns: "0.5fr 1fr 2.2fr 1.2fr 0.7fr 1.2fr 1.5fr", padding: "14px 20px", borderBottom: "1px solid #f5f5f5", alignItems: "center" },
  td          : { fontSize: 14, color: "#1a1a2e", display: "flex", alignItems: "center" },
  empty       : { textAlign: "center", padding: "60px 0", color: "#bbb", fontSize: 14 },
  pagination  : { display: "flex", justifyContent: "center", gap: 6, padding: "14px 0" },
  pageBtn     : { width: 30, height: 30, borderRadius: 6, border: "1px solid #e0e0e0", background: "#fff", cursor: "pointer", fontSize: 13, color: "#666" },
  pageBtnActive: { background: "#1677ff", color: "#fff", border: "1px solid #1677ff" },
  registerBtn : { background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  backBtn     : { background: "none", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: "#666", cursor: "pointer", marginBottom: 12, display: "block" },
  smAttBtn    : { background: "#f6ffed", border: "1px solid #b7eb8f", color: "#52c41a", borderRadius: 6, padding: "4px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
  smEditBtn   : { background: "#fff", border: "1px solid #1677ff", color: "#1677ff", borderRadius: 6, padding: "4px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
  smDelBtn    : { background: "#fff2f0", border: "1px solid #ffccc7", color: "#ff4d4f", borderRadius: 6, padding: "4px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
};

const ms = {
  overlay  : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" },
  modal    : { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460, margin: "0 16px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" },
  header   : { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #f0f0f0" },
  title    : { fontSize: 16, fontWeight: 700, color: "#1a1a2e" },
  closeBtn : { background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer" },
  body     : { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  field    : { display: "flex", flexDirection: "column", gap: 6 },
  label    : { fontSize: 13, fontWeight: 600, color: "#444" },
  input    : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", background: "#fafafa", color: "#1a1a2e" },
  footer   : { display: "flex", gap: 10, justifyContent: "flex-end", padding: "16px 24px", borderTop: "1px solid #f0f0f0" },
  cancelBtn: { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#666", cursor: "pointer" },
  saveBtn  : { background: "linear-gradient(135deg, #1677ff, #4096ff)", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, color: "#fff", fontWeight: 700, cursor: "pointer" },
};